<template>
  <v-flex xs12 row>
    <v-layout py-3>
      <v-flex xs12>
        <v-card>
          <v-card-title>
            
            <autocomplete
              v-model="filter.period"
              label="Time Period"
              search-key="type"
              :items="$options.periods"
            />
            
            <date-picker 
              v-if="filter.period === 'monthly'" 
              v-model="filter.year_month" 
              :min="minDateFrom" 
              :max="maxDate" 
              label="Year & Month" 
              class="mr-3" 
              type="month" 
            />

            <date-picker 
              v-else 
              v-model="filter.date_from" 
              :min="minDateFrom" 
              :max="maxDate" 
              :label="dateFromLabel" 
              class="mr-3" 
            />

            <date-picker 
              v-if="hasDateTo" 
              v-model="filter.date_to" 
              :disabled="!isDateToEditable"
              :min="minDateTo" 
              :max="maxDateTo" 
              label="Date To" 
              class="mr-3"
            />

            <autocomplete
              async
              clearable
              v-if="filter.sales_type !== config.invoiceTypes.franchisee"
              v-model="filter.retailer"
              label="Branch"
              sub-title="phone"
              key="retailer"
              :api-path="config.apis.searchRetailers"
            />
            
            <autocomplete
              v-model="filter.sales_type"
              label="Sale To"
              key="sales_type"
              :items="$options.types"
            />

            <autocomplete 
              async
              clearable
              v-if="filter.sales_type == config.invoiceTypes.customer"
              v-model="filter.customer"
              label="Customer Mobile"
              sub-title="title"
              search-key="mobile"
              key="customer-mobile"
              :api-path="config.apis.searchCustomers"
            />

            <autocomplete
              async
              clearable
              v-if="filter.sales_type == config.invoiceTypes.customer"
              v-model="filter.customer"
              label="Customer Name"
              sub-title="mobile"
              key="customer-name"
              :api-path="config.apis.searchCustomers"
            />

            <autocomplete
              async
              clearable
              v-if="filter.sales_type == config.invoiceTypes.franchisee"
              v-model="filter.franchisee"
              label="Franchisee"
              sub-title="phone"
              key="franchisee"
              :api-path="config.apis.searchRetailers"
            />

            <autocomplete
              async
              clearable
              v-model="filter.executive"
              label="Care of"
              sub-title="mobile"
              search-key="first_name"
              key="executive"
              :api-path="config.apis.searchExecutives"
            />

            <autocomplete
              async
              clearable
              v-model="filter.agent"
              label="LTP"
              sub-title="title"
              search-key="id"
              key="agent"
              :api-path="config.apis.searchAgents"
            />

            <search-btn @click="loadReport" />

            <v-spacer></v-spacer>

          </v-card-title>
          
          <data-table
            :headers="headers"
            :items="processedItems"
            :loading="processing"
            :title="resultTitle"
            :totals="totals"
            :sheet-name="sheetName"
            hide-actions
            download
          ></data-table>

      </v-card>
      </v-flex>
    </v-layout>
    
  </v-flex>
</template>

<script>

  import sumBy from 'lodash/sumBy'

  import { reports } from '../../config/apis'
  import invoiceTypes from '../../config/invoiceTypes'

  import reportMixin from '../../mixins/reports'
  import saleReturnStatusMixin from '../../mixins/sale-return-status'

  import DataTable from '../../components/DataTable'
  import DatePickerDialog from '../../components/DatePickerDialog'

  export default {
    
    components: {
      'data-table': DataTable,
      'date-picker': DatePickerDialog,
    },

    mixins: [ 
      reportMixin, 
      saleReturnStatusMixin,
    ],

    data () {
      return {
        title: "Sales Return Report",
        reportsApiPath: reports.salesReturn,
        filter: {
          period: "daily",
          date_from: this.today('YYYY-MM-DD'),
          date_to: '',
          year_month: '',
          sales_type: '',
          customer: null,
          franchisee: null,
          retailer: null,
          agent: null,
        },
        hasDateWiseReport: true,
      }
    },

    computed: {
    	
      processedItems() {

        if (this.filter.period === 'monthly') {
          return this.items
        }
          
        return this.items.map(item => {
          return {
            code: item.code,
            return_date: item.return_date,
            invoice_code: item.invoice_code,
            order_date: item.order_date,
            status: this.$options.filters.saleReturnStatusToText(item.status),
            customer_name: item.type == invoiceTypes.franchisee ? item.franchisee_name : item.customer_name,
            retailer_name: item.retailer_name,
            agent_name: item.agent_name,
            executive_name: item.executive_name,
            biller_name: item.biller_name,
            subtotal: item.subtotal,
            discount: item.discount,
            extra_discount: item.extra_discount,
            total: item.total,
            round_off: item.round_off,
            tax: item.tax,
            grandtotal: item.grandtotal
          }
        })
        
      },

      totals () {
        if (this.filter.period !== 'monthly' || !this.items.length) return {}

        return {
          return_date: "Total",
          return_count: sumBy(this.items, 'return_count'),
          day_subtotal: sumBy(this.items, 'day_subtotal'),
          day_discount: sumBy(this.items, 'day_discount'),
          day_extra_discount: sumBy(this.items, 'day_extra_discount'),
          day_total: sumBy(this.items, 'day_total'),
          day_round_off: sumBy(this.items, 'day_round_off'),
          day_tax: sumBy(this.items, 'day_tax'),
          day_grandtotal: sumBy(this.items, 'day_grandtotal')
        }

      },

    },

    watch: {

      'filter.sales_type' (val) {
        
        this.filter.franchisee = ""
        this.filter.customer = ""

        if (val === this.config.invoiceTypes.franchisee) {
          this.filter.retailer = ""
        }
      },

      'filter.retailer' (val) {

        if (!val) {
          this.filter.retailer = ""
        }
      },

    },

    created () {

      let types = [
          {
            id: "",
            title: "All"
          }
        ]

      for (const [key, val] of Object.entries(this.config.invoiceTypes)) {
        types.push({id: val, title: key})
      }

      this.$options.types = types

      this.$options.itemWiseHeaders = [
        { text: 'Return ID', value: 'code', sortable: false},
        { text: 'Return Date', value: 'return_date', sortable: false},
        { text: 'Order ID', value: 'invoice_code', sortable: false},
        { text: 'Order Date', value: 'order_date', sortable: false},
        { text: 'Status', value: 'status', sortable: false},
        { text: 'Customer/Franchisee', value: 'customer_name', sortable: false},
        { text: 'Branch', value: 'retailer_name', sortable: false},
        { text: 'LTP', value: 'agent_name', sortable: false, default: '-'},
        { text: 'Care of', value: 'executive_name', sortable: false, default: '-'},
        { text: 'Return Added', value: 'biller_name', sortable: false},
        { text: `Subtotal (${this.currency})`, value: 'subtotal', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Discount (${this.currency})`, value: 'discount', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Extra Discount (${this.currency})`, value: 'extra_discount', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Total (${this.currency})`, value: 'total', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Roundoff (${this.currency})`, value: 'round_off', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `GST (${this.currency})`, value: 'tax', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Grand Total (${this.currency})`, value: 'grandtotal', sortable: false, customFilter: 'roundValue', align: 'right'},
      ]

      this.$options.dateWiseHeaders = [
        { text: 'Return Date', value: 'return_date', sortable: false},
        { text: 'Returns', value: 'return_count', sortable: false, align: 'right'},
        { text: `Subtotal (${this.currency})`, value: 'day_subtotal', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Discount (${this.currency})`, value: 'day_discount', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Extra Discount (${this.currency})`, value: 'day_extra_discount', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Total (${this.currency})`, value: 'day_total', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Roundoff (${this.currency})`, value: 'day_round_off', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `GST (${this.currency})`, value: 'day_tax', sortable: false, customFilter: 'roundValue', align: 'right'},
        { text: `Grand Total (${this.currency})`, value: 'day_grandtotal', sortable: false, customFilter: 'roundValue', align: 'right'},
      ]

    },

  }
</script>
